// the smart crawl (Oct 27, programming-plan.md Phase B; settings in config/crawl.json). ws4kp's crawl keeps drawing
// the bar and calls one screen of ours every 4 s; that screen picks the line. Five tracks:
//   alert    the NWS hazard ws4kp found, a marine or beach advisory, AQI over 100, an SFO program or ground stop;
//            jumps the queue, every alertEverySeconds while it lasts
//   core     now (temp, sky, wind in one line; visibility and ceiling only when low), the next buses and cable car,
//            the next tide, the S&P while it trades (futures since the close around it), SFO, the next sunrise or
//            sunset, what's on next
//   context  what the screen above can't say: under a camera its place's data, under Markets the biggest mover
//   next up  once in the minute before each feature
//   color    the station line (once per 10 min), Now Playing (once per track), the forecast office on fog, a This
//            Day fact, the next crab window
// Core and the rest alternate. A screen declares the facts it shows (crawl.shows) and lines with those tags sit out
// while it's up. Short lines drop in whole (ws4kp's type-on) and hold for the daypart's ticks; a long line breaks into
// drops; only a piece that still won't fit, and ws4kp's hazard text, move, and moving text runs the full width of the
// bar (custom SCSS). No upstream file is patched: the base screens are replaced through ws4kp's own addScreen.
import tierOf, { beachAlert, dangers, alertWhen } from './advisories.mjs';
import { addScreen, reset, hazards } from '../currentweatherscroll.mjs';
import { currentDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import { getTimes } from '../../vendor/auto/suncalc.js';
import { nowPlaying, daypart } from './music.mjs';
import { data } from './snapshot.mjs';
import crabDays from './crab.mjs';
import sfoTickerLine from './sfo.mjs';
import marketState, { extraColumn } from './market-watch.mjs';
import { featureFor, busy } from './scheduler.mjs';
import { FEATURE_TITLES } from './cadence.mjs';
import { titleAt } from './shows.mjs';
import { bigShipDue, describe as shipText } from './under-the-gate.mjs';
import {
	time, weekday, isToday, feet, percent, compass, dayKey,
} from './format.mjs';

const {
	lineup, water, markets, crawl: cfg, clock,
} = window.RHL;
const MINUTE = 60000;
const DEGREE = '&deg;';
const when = (t, now = new Date()) => `${isToday(t, now) ? '' : `${weekday(t)} `}${time(t)}`;
const byRole = (role) => (data('buoys') ?? []).find((b) => b.role === role);

// hour and minute in the channel's time zone
const local = (now) => {
	const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
		timeZone: clock.timeZone, hour: 'numeric', minute: 'numeric', hourCycle: 'h23', weekday: 'short',
	}).formatToParts(now).map((p) => [p.type, p.value]));
	return { hour: Number(parts.hour), minute: Number(parts.minute), weekend: ['Sat', 'Sun'].includes(parts.weekday) };
};
const inHours = (now, ranges) => {
	const { hour } = local(now);
	return ranges.some(([from, to]) => hour >= from && hour < to);
};
const pace = (now) => {
	const { hour, minute, weekend } = local(now);
	const at = hour * 60 + minute;
	if (!weekend && at >= cfg.commute.from[0] * 60 + cfg.commute.from[1] && at < cfg.commute.to[0] * 60 + cfg.commute.to[1]) return 'commute';
	return daypart(now).name;
};
const night = (now) => daypart(now).name === 'night';

// ---- the lines: each returns { text, tags } or null ----

// now: one line from ws4kp's current conditions; visibility and ceiling only when they're low
const nowLine = (wx) => {
	if (!wx?.Temperature && wx?.Temperature !== 0) return null;
	const parts = [`Now ${wx.Temperature}${DEGREE}`];
	if (wx.TextConditions) parts.push(escapeHtml(wx.TextConditions.toLowerCase()));
	parts.push(wx.WindSpeed > 0 ? `wind ${wx.WindDirection} ${wx.WindSpeed}${wx.WindGust > 0 ? `, gusts ${wx.WindGust}` : ''}` : 'calm');
	const imperial = /mi/.test(wx.VisibilityUnit ?? '');
	const visMiles = imperial ? wx.Visibility : wx.Visibility * 0.621;
	const ceilFeet = /ft/.test(wx.CeilingUnit ?? '') ? wx.Ceiling : wx.Ceiling * 3.281;
	const low = [];
	if (Number.isFinite(visMiles) && visMiles < cfg.lowVisibilityMiles) low.push(`visibility ${wx.Visibility} ${String(wx.VisibilityUnit).replace(/\.$/, '').trim()}`);
	if (ceilFeet > 0 && ceilFeet < cfg.lowCeilingFeet) low.push(`ceiling ${Math.round(ceilFeet / 100) * 100} ft`);
	return { text: `${parts.join(', ')}${low.length ? ` &middot; ${low.join(', ')}` : ''}.`, tags: ['now'] };
};

const transitLine = (now) => {
	const byStop = Object.fromEntries((data('transit') ?? []).map((s) => [s.stop, s.arrivals ?? []]));
	const parts = cfg.transit.map(({ stop, name }) => {
		const mins = (byStop[stop] ?? []).map((a) => Math.floor((new Date(a.t) - now) / MINUTE)).filter((m) => m >= 0).slice(0, 2);
		if (!mins.length) return null;
		// each stop short enough to drop in whole (about 29 characters): a departures-board line, no "in" (Oct 27: "Cable
		// car to the Wharf in 21, 37 min" broke after the comma)
		if (mins[0] === 0) return `${name} now${mins[1] ? `, ${mins[1]} min` : ''}`;
		return `${name} ${mins.join(', ')} min`;
	}).filter(Boolean);
	return parts.length ? { text: `Next: ${parts.join(' &middot; ')}`, tags: ['transit'] } : null;
};

const tideLine = (now) => {
	const tides = data('tides');
	const next = tides?.events.find((e) => new Date(e.t) > now);
	return next ? { text: `${next.type === 'H' ? 'High' : 'Low'} tide ${when(next.t, now)}, ${feet(next.ft)}.`, tags: ['tide'] } : null;
};

// the S&P while the US session trades; around it, how far things moved since the close (ETFs, futures overnight)
const marketsLine = (now) => {
	const quotes = data('quotes');
	if (!quotes) return null;
	const state = marketState(now);
	if (state.stocks === 'Markets open' && quotes.SPX?.last) {
		return { text: `S&amp;P 500 ${Math.round(quotes.SPX.last).toLocaleString('en-US')}, ${percent(quotes.SPX.changePct, 2)}.`, tags: ['markets'] };
	}
	const extra = extraColumn(state);
	if (!extra || extra.key !== 'since') return null;
	const parts = markets.board.filter((i) => i.etf && quotes[i.key]?.since).map((i) => `${quotes[i.key].label ?? i.label} ${percent(quotes[i.key].since.pct, 2)}`);
	if (!parts.length) return null;
	const lead = { Pre: 'Pre-market', After: 'After hours', Fut: 'Futures since the close' }[extra.head];
	return { text: `${lead}: ${parts.join(', ')}.`, tags: ['markets'] };
};

const sfoLine = () => {
	const program = sfoTickerLine();
	if (program) return null; // the alert track carries it
	const sfo = (data('faa') ?? []).find((a) => a.airport === 'SFO');
	if (!sfo) return null;
	const dep = sfo.departures;
	return { text: dep ? `SFO departures running ${dep.avg ? `${dep.avg} min` : ''} late${dep.reason ? `, ${escapeHtml(dep.reason.replace(/^\w+:/, '').toLowerCase())}` : ''}.` : 'SFO: no FAA delays.', tags: ['sfo'] };
};

const sunLine = (now) => {
	const times = [0, 1].flatMap((d) => {
		const [y, m, day] = dayKey(now.getTime() + d * 86400000).split('-').map(Number);
		const t = getTimes(new Date(Date.UTC(y, m - 1, day, 20)), water.point.lat, water.point.lon);
		return [['Sunrise', t.sunrise], ['Sunset', t.sunset]];
	}).filter(([, t]) => t > now).sort((a, b) => a[1] - b[1]);
	const [name, t] = times[0] ?? [];
	return t ? { text: `${name} ${when(t, now)}.`, tags: ['sun'] } : null;
};

// the next feature on the clock, within the hour
const nextFeature = (now) => {
	for (let m = 1; m <= 60; m += 1) {
		const at = new Date(now.getTime() + m * MINUTE);
		const { minute } = local(at);
		const plan = featureFor(minute, at);
		if (plan) {
			const start = new Date(at);
			start.setSeconds(0, 0);
			const title = plan.name === 'show' ? titleAt(start) : FEATURE_TITLES[plan.name] ?? [plan.name];
			return { ...plan, at: start, title: title.join(' ').replace(/ on the 3s$/, '') };
		}
	}
	return null;
};
// a promo, so not at night; and not while a feature is on air (one that started early would read as still to come)
const onNextLine = (now) => {
	if (night(now) || busy()) return null;
	const next = nextFeature(now);
	return next ? { text: `Coming up: ${next.title} at ${time(next.at)}.`, tags: ['next'] } : null;
};

const CORE = [nowLine, transitLine, tideLine, marketsLine, sfoLine, sunLine, onNextLine];

// context: the camera's place
const placeOf = Object.fromEntries(Object.entries(cfg.places).flatMap(([place, ids]) => ids.map((id) => [id, place])));
const windNow = (now) => {
	const w = data('wind');
	if (!w?.times) return null;
	const i = w.times.reduce((best, t, idx) => (Math.abs(new Date(t) - now) < Math.abs(new Date(w.times[best]) - now) ? idx : best), 0);
	return Math.abs(new Date(w.times[i]) - now) < 2 * 3600000 ? { kt: w.kt[i], gust: w.gustKt?.[i], dir: w.dirDeg[i] } : null;
};
const PLACE_LINES = {
	gate: (now) => {
		const w = windNow(now);
		return w ? `Wind at the Gate ${compass(w.dir)} ${Math.round(w.kt)} kt${w.gust ? `, gusts ${Math.round(w.gust)}` : ''}.` : null;
	},
	water: (now) => {
		const waterF = (byRole('bar') ?? byRole('ocean'))?.waterF;
		const coming = (data('currents')?.events ?? []).filter((e) => new Date(e.t) > now);
		const max = coming.find((e) => e.type !== 'slack');
		const slack = coming.find((e) => e.type === 'slack');
		const parts = [
			waterF && `Water ${Math.round(waterF.value)}${DEGREE}`,
			max && `${max.type} ${max.kt.toFixed(1)} kt at ${time(max.t)}`,
			slack && `slack ${time(slack.t)}`,
		].filter(Boolean);
		return parts.length ? `${parts.join(' &middot; ')}.`.replace(/^./, (c) => c.toUpperCase()) : null;
	},
	coast: () => {
		const waves = (byRole('ocean') ?? byRole('bar'))?.waves;
		const rip = beachAlert(data('alerts'));
		const parts = [waves && `Surf ${Math.round(waves.ft)} ft at ${Math.round(waves.periodS)} s`, rip && escapeHtml(`${dangers(rip)} ${alertWhen(rip)}`.trim())].filter(Boolean);
		return parts.length ? `${parts.join(' &middot; ')}.` : null;
	},
	sfo: () => sfoTickerLine() ?? sfoLine()?.text ?? null,
	bridge: () => {
		const drive = data('drive');
		return drive?.minutes ? `Drive to SFO ${drive.minutes} min${drive.typicalMinutes ? `, usually ${drive.typicalMinutes}` : ''}.` : null;
	},
};
let camTurn = 0;
const lastContext = new Map();
const CONTEXT_REPEAT_MS = 45000;
const cameraContext = (display, now) => {
	const ids = [...(display?.elem?.querySelectorAll('.cam:not(.empty):not(.out):not(.cam-ahead) .cam-view[data-cam]') ?? [])]
		.filter((v) => v.offsetParent !== null).map((v) => v.dataset.cam);
	const places = [...new Set(ids.map((id) => placeOf[id]).filter(Boolean))];
	// each place in turn; the same line again only after CONTEXT_REPEAT_MS, so one camera doesn't hear it every
	// other drop
	for (let i = 0; i < places.length; i += 1) {
		camTurn += 1;
		const text = PLACE_LINES[places[camTurn % places.length]](now);
		if (text && now - (lastContext.get(text) ?? 0) >= CONTEXT_REPEAT_MS) {
			lastContext.set(text, now.getTime());
			return { text, tags: ['context'] };
		}
	}
	return null;
};
const biggestMover = () => {
	const quotes = data('quotes');
	const moves = markets.board.map((i) => ({ label: quotes?.[i.key]?.label ?? i.label, pct: quotes?.[i.key]?.changePct })).filter((m) => Number.isFinite(m.pct));
	if (!moves.length) return null;
	const top = moves.sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct))[0];
	const text = `Biggest mover today: ${escapeHtml(top.label)} ${percent(top.pct, 2)}.`;
	if (Date.now() - (lastContext.get(text) ?? 0) < CONTEXT_REPEAT_MS) return null;
	lastContext.set(text, Date.now());
	return { text, tags: ['context'] };
};
const contextLine = (display, now) => {
	const id = display?.elemId;
	if (id === 'views-block' || id === 'views-around-town') return cameraContext(display, now);
	if (id === 'market-closeup' || id === 'market-watch') return biggestMover();
	return null;
};

// alerts
const alertLines = (wx, now) => {
	const lines = [];
	if (wx?.hazards?.length) lines.push({ hazard: hazards(wx), tags: ['alert'] });
	const advisory = data('alerts')?.find((a) => (a.zones.includes(water.bayZone.id) || a.zones.includes('CAZ509')) && tierOf(a.event) === 'crawl');
	if (advisory) lines.push({ text: `${escapeHtml(advisory.event)}${advisory.ends ? ` until ${when(advisory.ends, now)}` : ''}.`, tags: ['alert', 'water'] });
	const air = data('air');
	const worst = air ? Math.max(air.aqi ?? 0, air.maxNext24 ?? 0) : 0;
	if (worst > 100) {
		const word = [[150, 'unhealthy for sensitive groups'], [200, 'unhealthy'], [300, 'very unhealthy'], [Infinity, 'hazardous']].find(([max]) => worst <= max)[1];
		lines.push({ text: air.aqi > 100 ? `Air quality ${word}: AQI ${air.aqi}${air.aqi > 150 ? ', smoke' : ''}.` : `Air quality turning ${word} within a day: AQI up to ${worst}.`, tags: ['alert', 'air'] });
	}
	const sfo = sfoTickerLine();
	if (sfo) lines.push({ text: sfo, tags: ['alert', 'sfo'] });
	return lines;
};

// color
const lastColor = new Map();
const colorDue = (key, minutes, now) => !lastColor.has(key) || now - lastColor.get(key) >= minutes * MINUTE;
let lastTrack = null;
const shipsAnnounced = new Set();
const colorLines = (now) => {
	const lines = [];
	const track = nowPlaying();
	const trackKey = track ? `${track.title}|${track.artist}` : null;
	if (track && trackKey !== lastTrack) {
		lines.push({
			key: 'track',
			mark: () => {
				lastTrack = trackKey;
			},
			text: `Now Playing: ${escapeHtml([track.title, track.artist].filter(Boolean).join(' - '))}`,
			tags: ['music'],
		});
	}
	if (!night(now) && colorDue('station', cfg.stationLineEveryMinutes, now) && lineup.ticker?.length) {
		lines.push({ key: 'station', text: escapeHtml(lineup.ticker[0]), tags: ['promo'] });
	}
	if (inHours(now, cfg.fogHours) && colorDue('fog', cfg.colorEveryMinutes.fog, now)) {
		const yes = new RegExp(cfg.fogWords, 'i');
		const no = new RegExp(cfg.fogNotWords, 'i');
		const fog = (data('fog')?.lines ?? []).find((l) => yes.test(l) && !no.test(l));
		if (fog) lines.push({ key: 'fog', text: `From the forecast office: ${escapeHtml(fog)}`, tags: ['fog'] });
	}
	const day = data('thisDay');
	if (day?.recordHigh && day.date === dayKey(now).slice(5) && colorDue('thisDay', cfg.colorEveryMinutes.thisDay, now)) {
		const pick = [
			day.recordHigh && `On this date: record high ${day.recordHigh.value}${DEGREE} in ${day.recordHigh.year}, record low ${day.recordLow.value}${DEGREE} in ${day.recordLow.year}.`,
			day.wettest && `On this date, the wettest was ${day.wettest.value.toFixed(2)} in of rain in ${day.wettest.year}.`,
		].filter(Boolean);
		lines.push({ key: 'thisDay', text: pick[Math.floor(now / (20 * MINUTE)) % pick.length], tags: ['thisDay'] });
	}
	// a big ship due under the Gate within 15 minutes, once per ship (Phase E)
	const ship = bigShipDue(1, 15, now);
	if (ship && !shipsAnnounced.has(ship.mmsi)) {
		lines.push({
			key: 'ship',
			mark: () => shipsAnnounced.add(ship.mmsi),
			text: `Under the Gate in ${ship.minutes} min: ${escapeHtml(shipText(ship))}${ship.name ? `, ${escapeHtml(ship.name)}` : ''}.`,
			tags: ['ships'],
		});
	}
	if (colorDue('crab', cfg.colorEveryMinutes.crab, now)) {
		const [next] = crabDays({ tides: data('tides'), swell: data('swell'), wind: data('wind') }, now);
		if (next) lines.push({ key: 'crab', text: `Next crab window: ${when(next.high.t, now)} high, ${next.score}/10.`, tags: ['crab'] });
	}
	return lines;
};

// ---- fitting: a line that won't fit the bar breaks into drops; a piece that still won't, moves ----
let measure = null;
// room for a drop: the bar's container less the drop's inset (the bar itself animates from zero width on each
// type-on, so it can't be measured directly)
let dropRoom = null;
const room = (bar) => {
	const style = getComputedStyle(bar);
	const inset = parseFloat(style.marginLeft) + parseFloat(style.marginRight);
	const container = bar.closest('.scroll-container') ?? bar.parentElement;
	// while a line moves the container is the full bar with no inset; a drop's room is the one measured last time
	// nothing was moving
	const moving = bar.closest('.scroll')?.classList.contains('rhl-moving') || bar.querySelector('.scroll-area');
	if (!moving) dropRoom = container.clientWidth - inset;
	return dropRoom ?? container.clientWidth - inset;
};
const widthOf = (html) => {
	const bar = document.querySelector('#container>.scroll .fixed');
	if (!bar?.parentElement?.clientWidth) return { width: 0, room: Infinity };
	if (!measure) {
		measure = document.createElement('span');
		measure.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;left:-9999px';
	}
	if (measure.parentElement !== bar) bar.append(measure);
	measure.innerHTML = html;
	return { width: measure.offsetWidth, room: room(bar) };
};
const fits = (html) => {
	const { width, room: space } = widthOf(html);
	return width <= space;
};
// break points, best first: between facts (the dot goes), after a label, sentences, clauses, then words
const SPLITS = [[' &middot; ', ''], [': ', ':'], ['. ', '.'], [', ', ','], [' ', '']];
const chunk = (html, level = 0) => {
	if (fits(html) || level >= SPLITS.length) return [html];
	const [sep, keep] = SPLITS[level];
	const pieces = html.split(sep);
	if (pieces.length < 2) return chunk(html, level + 1);
	// even pieces: aim each drop at the whole line's width over the number of drops it needs
	const { width, room: space } = widthOf(html);
	const target = width / Math.ceil(width / space);
	const out = [];
	let current = '';
	pieces.forEach((piece, i) => {
		const word = `${piece}${i < pieces.length - 1 ? keep : ''}`;
		const joined = current ? `${current}${sep === ' &middot; ' ? sep : ' '}${word}` : word;
		const over = widthOf(joined).width > Math.min(space, target * 1.3);
		if (current && over) {
			out.push(current);
			current = word;
		} else current = joined;
	});
	if (current) out.push(current);
	return out.flatMap((c) => (fits(c) ? [c] : chunk(c, level + 1)));
};

// ---- the picker ----
const lastAlert = new Map();
const nextUpDone = new Set();
let queue = [];
let holding = null;
let coreTurn = 0;
let otherNext = false;
const shownTags = (display) => new Set(cfg.shows[display?.elemId] ?? []);
const clear = (line, tags) => line && !line.tags.some((t) => tags.has(t));

const pick = (wx, now) => {
	const display = currentDisplay();
	const tags = shownTags(display);

	// alert: jumps the queue, every alertEverySeconds per alert
	const alert = alertLines(wx, now).find((l) => clear(l, tags) && now - (lastAlert.get(l.hazard?.header ?? l.text) ?? 0) >= cfg.alertEverySeconds * 1000);
	if (alert) {
		lastAlert.set(alert.hazard?.header ?? alert.text, now.getTime());
		return alert;
	}

	// next up: once, in the minute before a feature, not while one is on air, never at night
	if (!night(now) && !busy()) {
		const next = nextFeature(now);
		const key = next && `${next.name}@${next.at.getTime()}`;
		if (next && next.at - now <= MINUTE && !nextUpDone.has(key)) {
			if (nextUpDone.size > 50) nextUpDone.clear();
			nextUpDone.add(key);
			return { text: `Up next: ${next.title} at ${time(next.at)}.`, tags: ['next'] };
		}
	}

	// alternate core and the rest (context, else color, else core again); nights skip color but Now Playing
	const core = () => {
		for (let i = 0; i < CORE.length; i += 1) {
			coreTurn = (coreTurn + 1) % CORE.length;
			const line = CORE[coreTurn](CORE[coreTurn] === nowLine ? wx : now);
			if (clear(line, tags)) return line;
		}
		return null;
	};
	otherNext = !otherNext;
	if (otherNext) {
		const context = contextLine(display, now);
		if (context) return context;
		const color = colorLines(now).filter((l) => clear(l, tags) && (!night(now) || l.key === 'track'))[0];
		if (color) {
			if (color.key && color.key !== 'track' && color.key !== 'ship') lastColor.set(color.key, now.getTime());
			color.mark?.();
			return color;
		}
	}
	return core();
};

// called by ws4kp's crawl every 4 s (longer after a moving line)
let lastWx = null;
const screen = (wx) => {
	const now = new Date();
	lastWx = wx;
	if (holding && holding.ticks > 1) {
		holding.ticks -= 1;
		// the same line again, marked so the type-on doesn't replay
		return `<span class="rhl-line rhl-hold">${holding.html}</span>`;
	}
	if (!queue.length) {
		// never nothing: with ours the only screen, ws4kp would ask again at once
		const line = pick(wx, now) ?? { text: escapeHtml(lineup.ticker?.[0] ?? 'Russian Hill Local'), tags: [] };
		if (line.hazard) {
			holding = null;
			return { ...line.hazard, classes: [...(line.hazard.classes ?? []), 'rhl-moving'] };
		}
		queue = chunk(line.text);
	}
	const html = queue.shift();
	const ticks = cfg.holds[pace(now)] ?? 2;
	if (!fits(html)) {
		holding = null;
		return { type: 'scroll', text: html, classes: ['rhl-moving'] };
	}
	holding = { html, ticks };
	return `<span class="rhl-line">${html}</span>`;
};

const init = () => {
	reset();
	// keepBase false: ws4kp's six weather lines and its hazard screen give way to ours (the hazard comes back as the
	// alert track)
	addScreen(screen, false);
};

// custom-scroll-text.mjs resets the ticker in its own DOMContentLoaded handler, so this goes in after it
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => setTimeout(init));
} else {
	setTimeout(init);
}

// for the console: break a line as the crawl would, pick a line (it counts as aired), the pace and the facts the
// screen above is showing
window.RHL.crawlChunk = chunk;
window.RHL.crawlPick = (now = new Date()) => pick(lastWx, now);
window.RHL.crawlState = () => ({
	pace: pace(new Date()), shows: [...shownTags(currentDisplay())], queue: [...queue], holding,
});
